import type { DiscountCardStatus } from '@tempmee/core';
import type { IDiscountCode } from '../types/DiscountCode.types';

/**
 * The status chip a code renders as. The API sends no status field — the chip is
 * derived from `usesRemaining`, `live`, `endsAt` and `officeEligible`, in this
 * order (first match wins):
 *
 * 1. **used** — the per-office cap is spent (`usesRemaining === 0`). Wins over
 *    everything: a fully-used code the holder has burned is USED even if the
 *    campaign has since ended or the office has aged out — that is the fact the
 *    office acts on.
 * 2. **expired** — the campaign is no longer live, or `endsAt` has passed. Ended
 *    for everyone, so it outranks the office-specific `ineligible`.
 * 3. **ineligible** — the campaign is still live but this office no longer
 *    matches its audience (`live: true, officeEligible: false`). Nothing ended
 *    and nothing was spent; the code simply is not for them, and a claim would
 *    decline. Never fold this into `expired` — the promotion has not ended.
 * 4. **active** — anything else, including every uncapped code (no
 *    `usesRemaining` at all ⇒ unlimited ⇒ never "used").
 *
 * One rule for every surface that shows or counts a status — the Promo Codes
 * chips and the dashboard's active-promo tile — so the two can never disagree.
 *
 * @param code - one applied-code row from the API
 * @param now - injectable clock, so expiry is testable without freezing time globally
 */
export const deriveDiscountStatus = (
  code: Pick<IDiscountCode, 'live' | 'usesRemaining' | 'endsAt' | 'officeEligible'>,
  now: Date = new Date(),
): DiscountCardStatus => {
  // Strict equality on purpose: an uncapped code sends `usesRemaining: null`,
  // which must never read as a spent cap.
  if (code.usesRemaining === 0) {
    return 'used';
  }

  if (!code.live) {
    return 'expired';
  }

  if (code.endsAt && new Date(code.endsAt).getTime() <= now.getTime()) {
    return 'expired';
  }

  // Live campaign, not past its end date, but this office has dropped out of the
  // audience — a claim from here declines, so say so rather than showing ACTIVE.
  if (!code.officeEligible) {
    return 'ineligible';
  }

  return 'active';
};
